/**
 * Seeds the survey email form, and decides who may be its sender.
 *
 * Its own module because `SurveyFormEditor` may only export components
 * (react-refresh/only-export-components), and both halves are worth testing
 * directly: the From seed is where ENG-4001 went wrong.
 */
/**
 * Whether the API will still accept this person as the sender.
 *
 * `fromUsers` is the list the From picker itself is built from, and
 * `/users?scope=email_delegatable` has already narrowed it server-side to
 * connected mailboxes — so anyone missing from it is someone the save would
 * refuse.
 *
 * Absence is decisive for the signed-in user in particular: the delegation half
 * of that scope keeps the requesting user unconditionally
 * (`where(email_delegation_enabled: true).or(where(id: User.current.id))`), so
 * the only filter that can drop them is `connected_mailbox?`. An empty list
 * carries the same meaning for them, for the same reason — a connected mailbox
 * would have put them in it.
 *
 * Shared mailboxes are exempt because the API exempts them: with
 * `ats_shared_email_id` set the sender on the record is the current user and
 * `verify_mailbox_connection` never runs.
 */
export const canSendAs = (
  sender: { id?: string | number; isSharedEmail?: boolean } | null | undefined,
  senders: Array<{ id?: string | number; isSharedEmail?: boolean }> | undefined
): boolean => {
  if (!sender?.id) return false;
  if (sender?.isSharedEmail) return true;
  if (!Array.isArray(senders)) return false;

  // Shared mailboxes ride in this same list under their own ids, so an id match
  // alone would let a disconnected user through on a mailbox's number.
  return senders.some(
    (user) => !user?.isSharedEmail && String(user?.id) === String(sender.id)
  );
};

export const getSurveyEmailInitialValue = (
  activity,
  selectedTemplate,
  variablesListArr,
  currUser,
  fromUsers
) => {
  const {
    candidate_comm_sender = undefined,
    cc_participants,
    bcc_participants,
    candidate_comm_template,
    ats_shared_email = undefined,
    to_participants = undefined,
  } = activity;

  // Saved `candidate_comm_sender` may point to a user whose
  // `email_delegation_enabled` was turned off after the activity was
  // configured. The BE's `users?scope=email_delegatable` list (and our
  // `fromUsers`) filters those out, so pre-filling their id leaves the
  // From dropdown showing a value that isn't a valid option. When that
  // happens we leave From empty so the required-field validation forces
  // the user to pick a valid sender before submit.
  const hasSavedSender = !!candidate_comm_sender?.id;

  let senderIdForFrom: string | undefined;
  let senderObjectForFrom: any;
  if (hasSavedSender) {
    const isSavedSenderDelegatable = canSendAs(
      candidate_comm_sender,
      fromUsers
    );
    senderIdForFrom = isSavedSenderDelegatable
      ? candidate_comm_sender.id
      : undefined;
    senderObjectForFrom = isSavedSenderDelegatable
      ? candidate_comm_sender
      : undefined;
  } else {
    // The same test the saved sender gets. Seeding the signed-in user
    // unconditionally is what put an unusable From on a brand-new survey: the
    // save then 422s with "Sender's mailbox is not connected" after the whole
    // email has been written (ENG-4001).
    const canCurrUserSend = canSendAs(currUser, fromUsers);
    senderIdForFrom = canCurrUserSend ? currUser?.id : undefined;
    senderObjectForFrom = canCurrUserSend ? currUser : undefined;
  }

  const emailPayload = {
    from: ats_shared_email?.id || senderIdForFrom,
    fromUserObject: ats_shared_email?.id
      ? { ...ats_shared_email, isSharedEmail: true }
      : senderObjectForFrom,
    cc: cc_participants?.map((item) => item?.id),
    bcc: bcc_participants?.map((item) => item?.id),
    toAddress: to_participants?.map((item) => item?.id),
    variablesListArr,
  };

  if (!activity?.id || selectedTemplate?.id) {
    emailPayload['subject'] = selectedTemplate?.subject || '';
    emailPayload['body'] = selectedTemplate?.body || '';
    emailPayload['attachments'] = selectedTemplate?.attachments || [];
  } else {
    const { subject, body, attachments } = candidate_comm_template || {};
    emailPayload['subject'] = subject;
    emailPayload['body'] = body;
    emailPayload['attachments'] = attachments;
  }
  return emailPayload;
};
